import { RootState } from '@/lib/store';
import { setSettings } from '@/lib/store/theme/actions';
import { Nullable } from '@starsoft/common/interfaces';
import { Setting } from '@starsoft/common/models';
import { Operator } from '@starsoft/common/models/operator';
import { OriginIdType } from '@starsoft/common/models/origin/id';
import { Origin } from '@starsoft/common/models/origin';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GamingAggregator } from '@/models/gaming-aggregator';
import { setCookie } from 'cookies-next';
import { CookiesKey } from '@starsoft/common/enums';
import { GamingAggregatorStatusId } from '@/models/gaming-aggregator/status/id';
import { GamingAggregatorId } from '@/models/gaming-aggregator/id';

export function useResolvedSettings(setting?: Setting) {
  const dispatch = useDispatch();
  const stateSetting = useSelector((state: RootState) => state.theme.setting);

  const origin: Nullable<Origin> = useMemo(() => {
    return setting?.origin || stateSetting?.origin;
  }, [setting, stateSetting]);

  const originId: Nullable<OriginIdType> = useMemo(() => {
    return origin?.id;
  }, [origin]);

  const aggregators: Array<GamingAggregator> = useMemo(() => {
    return setting?.aggregators || stateSetting?.aggregators;
  }, [setting, stateSetting]);

  const operator: Nullable<Operator> = useMemo(() => {
    return setting?.operator || stateSetting?.operator;
  }, [setting, stateSetting]);

  const handleInitStateSetting = useCallback(() => {
    setCookie(CookiesKey.OriginId, originId?.toString() ?? '', {
      sameSite: 'strict',
    });
    if (!setting) {
      return;
    }

    dispatch(setSettings(setting));
  }, [setting, dispatch, originId]);

  const isStarsoftActive = useMemo(() => {
    return (
      aggregators
        ?.find(
          aggregator =>
            aggregator.id?.toString() ===
            GamingAggregatorId.Starsoft?.toString(),
        )
        ?.status?.id?.toString() === GamingAggregatorStatusId.Active?.toString()
    );
  }, [aggregators]);

  useEffect(handleInitStateSetting, [handleInitStateSetting]);

  return {
    setting: setting ?? stateSetting,
    origin,
    operator,
    originId,
    aggregators,
    isStarsoftActive,
  };
}
